import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import req from '@sportninja/common/api/request'
import { AnalyticsData } from '@sportninja/common/types/Registration'
import {
  RegistrationData,
  extractValues,
} from '@sportninja/common/hooks/useRegistrationData'
import { registrationReducer, initialState } from './reducer'
import {
  RegistrationContextValue,
  ActionType,
  UpdateTeamStatusParams,
  UpdatePlayerStatusParams,
  RefetchOptions,
} from './types'
import { computeDivisions } from '../../utils/computeDivisions'

const RegistrationContext = createContext<RegistrationContextValue | undefined>(
  undefined
)

interface RegistrationProviderProps {
  children: React.ReactNode
  scheduleId: string
}

export const RegistrationProvider: React.FC<RegistrationProviderProps> = ({
  children,
  scheduleId,
}) => {
  const [state, dispatch] = useReducer(registrationReducer, {
    ...initialState,
    scheduleId,
  })

  const fetchData = useCallback(
    async (options: boolean | RefetchOptions = true) => {
      if (!scheduleId) {
        return
      }

      // Support both boolean (legacy) and options object
      const normalizedOptions: RefetchOptions =
        typeof options === 'boolean' ? { displayLoading: options } : options
      const {
        displayLoading = true,
        onBeforeFetch,
        onAfterFetch,
      } = normalizedOptions

      // Call before fetch callback (e.g., capture scroll position)
      onBeforeFetch?.()

      if (displayLoading) {
        dispatch({ type: ActionType.SET_LOADING, payload: true })
      }
      dispatch({ type: ActionType.SET_ERROR, payload: null })

      try {
        const [detailedResponse, infoResponse, analyticsResult] =
          await Promise.all([
            req(`/registration/${scheduleId}/admin/detailed-new`),
            req(`/registration/${scheduleId}/admin/info`),
            req(`/registration/${scheduleId}/admin/analytics`).catch(
              () => null
            ),
          ])

        detailedResponse.data.registration = infoResponse.data
        detailedResponse.data.available_taxes =
          infoResponse.data.available_taxes

        const registrationData: RegistrationData = {
          detailed: detailedResponse.data,
          analytics: analyticsResult?.data || ({} as AnalyticsData),
        }

        dispatch({ type: ActionType.SET_DATA, payload: registrationData })
        dispatch({
          type: ActionType.SET_ANALYTICS,
          payload: analyticsResult?.data || null,
        })

        // Compute divisions from data
        const divisions = computeDivisions(registrationData)
        dispatch({
          type: ActionType.SET_DIVISIONS,
          payload: divisions || [],
        })

        // Call after fetch callback (e.g., restore scroll position)
        onAfterFetch?.()
      } catch (err: unknown) {
        dispatch({
          type: ActionType.SET_ERROR,
          payload:
            err instanceof Error
              ? err
              : new Error('Failed to fetch registration data'),
        })
        // Still restore scroll on error
        onAfterFetch?.()
      } finally {
        if (displayLoading) {
          dispatch({ type: ActionType.SET_LOADING, payload: false })
        }
      }
    },
    [scheduleId]
  )

  const fetchTypes = useCallback(async () => {
    dispatch({ type: ActionType.SET_LOADING_TYPES, payload: true })
    try {
      const response = await req('/types/registration')
      dispatch({ type: ActionType.SET_TYPES, payload: extractValues(response) })
    } catch {
      // Error handled by finally block
    } finally {
      dispatch({ type: ActionType.SET_LOADING_TYPES, payload: false })
    }
  }, [])

  const refetchAnalytics = useCallback(async () => {
    if (!scheduleId) {
      return
    }
    dispatch({ type: ActionType.SET_LOADING_ANALYTICS, payload: true })
    try {
      const analyticsResponse = await req(
        `/registration/${scheduleId}/admin/analytics`
      )
      dispatch({
        type: ActionType.SET_ANALYTICS,
        payload: analyticsResponse.data || null,
      })
    } catch {
      // Analytics failure is non-critical
    } finally {
      dispatch({ type: ActionType.SET_LOADING_ANALYTICS, payload: false })
    }
  }, [scheduleId])

  const updateTeamStatus = useCallback(
    async (params: UpdateTeamStatusParams) => {
      dispatch({
        type: ActionType.SET_STATUS_UPDATE_LOADING,
        payload: { id: params.teamRegistrationId, loading: true },
      })

      try {
        const body: Record<string, unknown> = {
          team_registration_id: params.teamRegistrationId,
          status: params.status,
          conditions: params.conditions,
          condition_type_id: params.conditionType,
          refund_all_funds: params.refundAllFunds,
        }

        if (params.divisionId) {
          body.division_id = params.divisionId
        }

        await req(`/registration/${scheduleId}/admin/team-status`, {
          method: 'PUT',
          body: JSON.stringify(body),
        })

        // Refetch data after status update with scroll preservation
        await fetchData({
          displayLoading: false,
          onBeforeFetch: params.onBeforeRefetch,
          onAfterFetch: params.onAfterRefetch,
        })
      } finally {
        dispatch({
          type: ActionType.SET_STATUS_UPDATE_LOADING,
          payload: { id: params.teamRegistrationId, loading: false },
        })
      }
    },
    [scheduleId, fetchData]
  )

  const updatePlayerStatus = useCallback(
    async (params: UpdatePlayerStatusParams) => {
      dispatch({
        type: ActionType.SET_STATUS_UPDATE_LOADING,
        payload: { id: params.teamRegistrationId, loading: true },
      })

      try {
        const body: Record<string, unknown> = {
          team_registration_id: params.teamRegistrationId,
          status: params.status,
          conditions: params.conditions,
          condition_type_id: params.conditionType,
          refund_all_funds: params.refundAllFunds,
        }

        if (params.divisionId) {
          body.division_id = params.divisionId
        }

        await req(`/registration/${scheduleId}/admin/player-status`, {
          method: 'PUT',
          body: JSON.stringify(body),
        })

        // Refetch data after status update with scroll preservation
        await fetchData({
          displayLoading: false,
          onBeforeFetch: params.onBeforeRefetch,
          onAfterFetch: params.onAfterRefetch,
        })
      } finally {
        dispatch({
          type: ActionType.SET_STATUS_UPDATE_LOADING,
          payload: { id: params.teamRegistrationId, loading: false },
        })
      }
    },
    [scheduleId, fetchData]
  )

  // Fetch data and types on mount
  useEffect(() => {
    fetchData()
    fetchTypes()
  }, [fetchData, fetchTypes])

  const value: RegistrationContextValue = useMemo(
    () => ({
      ...state,
      refetch: fetchData,
      refetchAnalytics,
      updateTeamStatus,
      updatePlayerStatus,
    }),
    [state, fetchData, refetchAnalytics, updateTeamStatus, updatePlayerStatus]
  )

  return (
    <RegistrationContext.Provider value={value}>
      {children}
    </RegistrationContext.Provider>
  )
}

export const useRegistrationContext = () => {
  const context = useContext(RegistrationContext)
  if (context === undefined) {
    throw new Error(
      'useRegistrationContext must be used within RegistrationProvider'
    )
  }
  return context
}
